import { MainContainer } from '@/components';
import { AccountFundingDetails } from '@/components/Account/AccountFundingDetails';
import getConfigValue from '@/config';
import { getRemainingRequirements } from '@/helpers/getRemainingRequirements';
import { hasMaxMonthlyPaymentCap } from '@/helpers/hasMaxMonthlyPaymentCap';
import { intercomTarget } from '@/helpers/intercomTarget';
import { isMonthlyPaymentCapMet } from '@/helpers/isMonthlyPaymentCapMet';
import usePermissions from '@/hooks/usePermissions';
import { useGetAccountQuery, useGetBundleGroupAccountsQuery } from '@/services/accounts';
import { downloadFile } from '@/services/helpers';
import { useGetOrganizationDetailsQuery } from '@/services/organizations';
import { useGetQuotesQuery } from '@/services/quotes';
import { AccountStatus } from '@/types/Accounts';
import { Alert, Badge, Box, Button, Icon, Modal, TextInput, toast } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, NavLink, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  AccountDocument,
  AccountMilestoneStatus,
  AccountMilestoneWithRequirements,
  AccountRequirementDocument,
  AccountRequirementStatus,
  ExternalReferenceType,
  MilestoneRequirement,
  ProgramType,
  ProgramTypeLabels,
  QuoteStatus,
  UserPermissions,
} from 'types';

import { AddersTab } from '../Adders/AddersTab';
import { useAuth } from '../auth/authProvider';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import CancelledBanner from '../Cancel/CancelledBanner';
import { LeadManagementRejectLeadModal } from '../LeadManagement/LeadManagementRejectLeadModal';
import PageHeader from '../PageHeader';
import { PricingCalculatorFactory } from '../Quotes/PricingCalculatorFactory';
import AdminStipulationBannerList from '../Stipulations/AdminStipulationBannerList';

import { AccountStatusIndicators } from './AccountStatusIndicators';
import { CustomerPaymentTab } from './CustomerPaymentTab';
import { exportCongaTestCsv } from './exportCongaTestCsv';
import { ExportCongaTestCsvModal } from './ExportCongaTestCsvModal';
import { GetInviteLinkModal } from './GetInviteLinkModal';
import History from './History';
import { CreateBundledAccountModal } from './modals/CreateBundledAccountModal';
import Overview from './Overview';
import { SystemDesignFactory } from './SystemDesignFactory';
import Warranties from './Warranties';

const api = getConfigValue('VITE_APP_API_BASE_URL');

const inviteDisabledStatuses = new Set([
  AccountStatus.created,
  AccountStatus.creditApproved,
  AccountStatus.creditApprovedWithStipulations,
  AccountStatus.creditDenied,
  AccountStatus.contractCreated,
]);

/**
 * @description Returns whether we just voided a contract or not.
 */
export function useJustVoidedContract() {
  const location = useLocation();
  return () => {
    return (location && location.state && location.state['justVoidedContract']) || false;
  };
}

/**
 * @description Returns whether the 1-year estimate has been updated or not.
 */
export function useJustUpdated1YearEstimate() {
  const location = useLocation();
  return () => {
    return (location && location.state && location.state['justUpdated1yEstimate']) || false;
  };
}

export const getSystemDesignLabel = (programType: ProgramType) => {
  switch (programType) {
    case ProgramType.energyBackup:
    case ProgramType.hvac:
    case ProgramType.waterHeater:
    case ProgramType.generator:
    case ProgramType.geothermal:
      return 'System Design';
    case ProgramType.doePr:
    case ProgramType.newCommunity:
    case ProgramType.newHomes:
    case ProgramType.solar:
    case ProgramType.consumerSolar:
      return 'PV System';
    default:
      return '';
  }
};

const isSystemDesignMissing = (account: AccountDocument) => {
  switch (account?.programType) {
    case ProgramType.hvac:
    case ProgramType.waterHeater:
    case ProgramType.generator: {
      const hvacAccount = account as AccountDocument<ProgramType.hvac>;
      return !hvacAccount.systemDesign || hvacAccount.systemDesign?.systems?.length === 0;
    }
    case ProgramType.energyBackup: {
      const energyBackupAccount = account as AccountDocument<ProgramType.energyBackup>;
      return !energyBackupAccount.systemDesign || energyBackupAccount.systemDesign?.equipment?.length === 0;
    }
    case ProgramType.geothermal: {
      const geothermalAccount = account as AccountDocument<ProgramType.geothermal>;
      return !geothermalAccount.systemDesign || !geothermalAccount.systemDesign?.systems?.length;
    }
    default: {
      const solarAccount = account as AccountDocument<
        ProgramType.solar | ProgramType.doePr | ProgramType.newHomes | ProgramType.newCommunity
      >;
      return !solarAccount?.systemDesign?.systemSizeKw || !solarAccount?.systemDesign?.systemFirstYearProductionKwh;
    }
  }
};

export function HandleToastDisplayComponent() {
  const hasVoidedAccountCheck = useJustVoidedContract();
  const hasUpdatedEstimateCheck = useJustUpdated1YearEstimate();
  const showVoidedContract = hasVoidedAccountCheck();
  const showUpdated1YREstimate = hasUpdatedEstimateCheck();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (showUpdated1YREstimate) {
      toast.success('Year-1 Production Estimate Updated', {
        style: {
          minWidth: 'fit-content',
        },
      });
    }
    if (showVoidedContract) {
      toast.success('Contract Voided', {
        style: {
          minWidth: 'fit-content',
        },
      });
    }
    if (showVoidedContract || showUpdated1YREstimate) {
      navigate(location, {
        replace: true,
      });
    }
  }, [showVoidedContract, showUpdated1YREstimate, navigate, location]);
  return null;
}

export function Account() {
  const { id } = useParams<{ id: any }>();
  const {
    resendConsumerInviteButtonEnabled,
    viewConsumerInviteLink,
    viewAccountActivity = false,
    viewAccountsSummary,
    enableAccountAttributesBadges = false,
    payoutsEpcAdminView,
    monthlyPaymentCap,
    enableWarrantyRegistration = false,
    solarPricingCalculator = false,
    customerPayments = false,
    enableLeadManagement = false,
  } = useFlags();

  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(id);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isCongaExportModalOpen, setIsCongaExportModalOpen] = useState(false);
  const [isCongaExporting, setIsCongaExporting] = useState(false);
  const programType = account?.programType;
  const hasConcordAccountBeenCreated = account?.externalReferenceIds?.find(
    (ref: any) => ref.type === ExternalReferenceType.concord,
  )?.id;
  const dispatchDispatchId = account?.externalReferenceIds?.find(
    (ref: any) => ref.type === ExternalReferenceType.dispatchId,
  )?.id;
  const { data: quotes, isLoading: isQuotesLoading } = useGetQuotesQuery(
    { id, programType },
    { skip: !programType },
  );
  const { claims, user } = useAuth();
  const userPermissions = usePermissions();
  const isPalmettoUser = isPalmettoFinanceUser(claims);

  const isBundled = Boolean(account?.bundleGroupId);

  const { data: bundleGroupData } = useGetBundleGroupAccountsQuery(id ?? '', {
    skip: !isBundled || !id,
  });

  const { data: organizationDetails } = useGetOrganizationDetailsQuery(
    { alias: account?.organizationId ?? '' },
    { skip: !account?.organizationId },
  );

  const canBundle = !account?.bundleGroupId && Boolean(organizationDetails?.accountBundling?.enabled);

  const [isLinkedAccountsOpen, setIsLinkedAccountsOpen] = useState(false);
  const linkedAccountsHoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleLinkedAccountsMouseEnter = useCallback(() => {
    if (linkedAccountsHoverTimerRef.current) clearTimeout(linkedAccountsHoverTimerRef.current);
    setIsLinkedAccountsOpen(true);
  }, []);
  const handleLinkedAccountsMouseLeave = useCallback(() => {
    linkedAccountsHoverTimerRef.current = setTimeout(() => setIsLinkedAccountsOpen(false), 150);
  }, []);

  const handleExportForTesting = useCallback(() => {
    setIsExportModalOpen(true);
  }, []);

  const handleExportConfirm = useCallback(
    async (overrides: { firstName: string; lastName: string; email: string; phoneNumber: string }) => {
      setIsExportModalOpen(false);
      const params = new URLSearchParams();
      if (overrides.firstName) params.set('firstName', overrides.firstName);
      if (overrides.lastName) params.set('lastName', overrides.lastName);
      if (overrides.email) params.set('email', overrides.email);
      if (overrides.phoneNumber) params.set('phoneNumber', overrides.phoneNumber);
      const qs = params.toString();

      try {
        setIsDownloading(true);
        await downloadFile(
          `${api}/internal/accountctl/accounts/${id}/dump${qs ? `?${qs}` : ''}`,
          `account-${id}-dump.json`,
        );
      } catch (error) {
        console.error('Error exporting account for testing.', error);
        toast.error('Error exporting account for testing. Please try again later.');
      } finally {
        setIsDownloading(false);
      }
    },
    [id],
  );

  const handleOpenCongaExportModal = useCallback(() => setIsCongaExportModalOpen(true), []);
  const handleCloseCongaExportModal = useCallback(() => setIsCongaExportModalOpen(false), []);
  const handleCongaExportConfirm = useCallback(
    async (email: string) => {
      setIsCongaExportModalOpen(false);
      if (!id) return;
      setIsCongaExporting(true);
      try {
        await exportCongaTestCsv(api, id, email);
      } finally {
        setIsCongaExporting(false);
      }
    },
    [id],
  );

  const [isBundleModalOpen, setIsBundleModalOpen] = useState(false);
  const handleOpenBundleModal = useCallback(() => setIsBundleModalOpen(true), []);
  const handleCloseBundleModal = useCallback(() => setIsBundleModalOpen(false), []);

  const [isRejectLeadModalOpen, setIsRejectLeadModalOpen] = useState(false);
  const handleOpenRejectLeadModal = useCallback(() => setIsRejectLeadModalOpen(true), []);
  const handleCloseRejectLeadModal = useCallback(() => setIsRejectLeadModalOpen(false), []);
  const handleConfirmRejectLead = useCallback(() => {
    setIsRejectLeadModalOpen(false);
  }, []);

  const activeQuote = quotes?.find((quote) => quote.status === QuoteStatus.active);
  const hasActiveQuoteAdders = activeQuote?.adders?.length > 0;
  const primaryAccountApplicant = account?.applicants?.find((applicant: any) => applicant.type === 'primary');
  const isLoading = isAccountLoading || isQuotesLoading;
  const contractSent = !inviteDisabledStatuses.has(account?.status);

  const canViewWarrantiesTab =
    (programType === ProgramType.hvac || programType === ProgramType.waterHeater) &&
    isPalmettoUser &&
    enableWarrantyRegistration;

  const wasMonthlyPaymentCapExceeded =
    useMemo(() => {
      if (account && activeQuote && activeQuote.systemPricingDetails) {
        return !isMonthlyPaymentCapMet(
          account,
          activeQuote.escalationRate,
          activeQuote.systemPricingDetails,
          monthlyPaymentCap,
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account, activeQuote?.escalationRate, activeQuote?.systemPricingDetails, monthlyPaymentCap]) || false;

  const remainingRequirements =
    useMemo(() => {
      if (account && !isAccountLoading) {
        const currentMilestone = account.milestones?.find(
          (milestone: any) => milestone.type === account.currentMilestone.type,
        );
        return getRemainingRequirements(currentMilestone);
      }
    }, [isAccountLoading, account]) || [];

  const isComfortPlan =
    programType === ProgramType.hvac ||
    programType === ProgramType.waterHeater ||
    programType === ProgramType.generator ||
    programType === ProgramType.geothermal;
  const showSolarCalculator =
    [ProgramType.solar, ProgramType.newHomes, ProgramType.newCommunity].includes(programType) &&
    solarPricingCalculator;

  const isAtMaxMonthlyPaymentCap = useMemo(() => {
    return hasMaxMonthlyPaymentCap(account);
  }, [account]);

  const isEpcAssigned = useMemo(() => {
    return (
      account?.milestones
        ?.flatMap((milestone: AccountMilestoneWithRequirements) => milestone.requirements)
        ?.some(
          (requirement: AccountRequirementDocument) =>
            requirement.type === MilestoneRequirement.epcAssigned &&
            requirement.status === AccountRequirementStatus.completed,
        ) ?? false
    );
  }, [account?.milestones]);

  const showPvSystemWarning =
    !isLoading &&
    (programType === ProgramType.consumerSolar
      ? isEpcAssigned && isSystemDesignMissing(account)
      : isSystemDesignMissing(account));
  const pageHeaderTitleProps = useMemo(
    () => (programType ? { 'data-intercom-target': intercomTarget('account', 'page-header-title') } : undefined),
    [programType],
  );

  return (
    <>
      <Helmet>
        <title>{`${account?.primaryApplicantName ?? 'Palmetto Finance Portal'}`}</title>
      </Helmet>
      <MainContainer
        data-intercom-program={programType ?? undefined}
        data-intercom-state={account?.address?.state ?? undefined}
      >
        {account?.cancellation?.isCancelled && <CancelledBanner cancellation={account?.cancellation} />}
        <AdminStipulationBannerList />
        <HandleToastDisplayComponent />
        <Box
          direction={{ base: 'column', tablet: 'row' }}
          childGap={{ base: 'sm', tablet: 'xl' }}
          alignaccounts={{ tablet: 'center' }}
        >
          <PageHeader
            eyebrow={<Link to="/accounts">Accounts</Link>}
            title={account?.primaryApplicantName ?? '...'}
            titleProps={pageHeaderTitleProps}
            description={
              enableAccountAttributesBadges
                ? null
                : viewAccountsSummary &&
                  remainingRequirements?.length > 0 && (
                    <Box direction="row" padding="xs 0 0 0" wrap style={{ gap: 'var(--size-spacing-xs)' }}>
                      {remainingRequirements?.map((requirement) => (
                        <Badge
                          variant="warning"
                          key={`${requirement?.order}-${requirement?.type}`}
                          message={requirement?.name}
                          size={{ base: 'md', desktop: 'lg' }}
                        />
                      ))}
                    </Box>
                  )
            }
          >
            {isBundled && (
              <Box
                position="relative"
                style={{ display: 'inline-block' }}
                onMouseEnter={handleLinkedAccountsMouseEnter}
                onMouseLeave={handleLinkedAccountsMouseLeave}
              >
                <Button
                  size={{ base: 'sm', desktop: 'md' }}
                  iconPrefix="link"
                  iconSuffix={isLinkedAccountsOpen ? 'caret-up' : 'caret-down'}
                  isDisabled={isLoading}
                  type="button"
                >
                  Linked accounts
                </Button>
                {isLinkedAccountsOpen && (
                  <Box
                    position="absolute"
                    background="white"
                    borderWidth="xs"
                    borderColor="separator"
                    radius="md"
                    padding="sm"
                    style={{
                      top: 'calc(100% + 4px)',
                      right: 0,
                      zIndex: 100,
                      minWidth: '220px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    }}
                  >
                    {(bundleGroupData?.accounts ?? []).map((bundledAccount: AccountDocument) => (
                      <Link
                        key={String(bundledAccount.id)}
                        to={`/accounts/${String(bundledAccount.id)}`}
                        style={{
                          textDecoration: 'none',
                          color: 'inherit',
                          fontWeight: String(bundledAccount.id) === id ? 600 : 400,
                        }}
                      >
                        <Box
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                          padding="xs sm"
                          radius="sm"
                          hover={{ background: 'grey-50' }}
                        >
                          <Box
                            fontSize="sm"
                            direction="row"
                            alignItems="center"
                            gap="xs"
                            style={{ textDecoration: 'underline' }}
                          >
                            {ProgramTypeLabels[bundledAccount.programType as ProgramType] ? (
                              <>
                                <Box>
                                  <Icon name="link" size="xs" />
                                </Box>
                                <Box>{ProgramTypeLabels[bundledAccount.programType as ProgramType]} account</Box>
                              </>
                            ) : null}
                          </Box>
                          <Box fontSize="xs" color="body-secondary" alignItems="center" direction="row" gap="xs">
                            <Icon
                              name={
                                bundledAccount.currentMilestone?.status === AccountMilestoneStatus.approved
                                  ? 'c-check'
                                  : 'c-in-progress'
                              }
                              size="xs"
                            />{' '}
                            {bundledAccount.currentMilestone?.name ?? '—'}
                          </Box>
                        </Box>
                      </Link>
                    ))}
                    <Box
                      as="button"
                      type="button"
                      direction="row"
                      alignItems="center"
                      childGap="xs"
                      padding="xs sm"
                      radius="sm"
                      fontSize="sm"
                      hover={{ background: 'grey-50' }}
                      style={{
                        width: '400px',
                        maxWidth: '400px',
                        cursor: 'pointer',
                        background: 'none',
                        border: 'none',
                        borderTop: '1px solid var(--color-border-separator)',
                        marginTop: 'var(--size-spacing-xs)',
                        paddingTop: 'var(--size-spacing-xs)',
                      }}
                      onClick={handleOpenBundleModal}
                    >
                      <Icon name="add" size="sm" />
                      Bundle another account
                    </Box>
                  </Box>
                )}
              </Box>
            )}
            {canBundle && (
              <Button
                variant="secondary"
                size={{ base: 'sm', desktop: 'md' }}
                iconPrefix="add"
                onClick={handleOpenBundleModal}
                isDisabled={isLoading}
                type="button"
              >
                Bundle Accounts
              </Button>
            )}
          </PageHeader>

          <Box padding={{ base: '0 lg', tablet: '0' }} direction="row" gap="sm" alignItems="center">
            {(resendConsumerInviteButtonEnabled || viewConsumerInviteLink) && (
              <GetInviteLinkModal
                contractSent={contractSent}
                isLoading={isLoading}
                email={primaryAccountApplicant?.email}
              />
            )}
            {enableLeadManagement && dispatchDispatchId && (
              <Button
                variant="secondary"
                tone="danger"
                size={{ base: 'sm', desktop: 'md' }}
                iconPrefix="remove"
                onClick={handleOpenRejectLeadModal}
                isDisabled={isLoading}
              >
                Reject Lead
              </Button>
            )}
            {isPalmettoUser && (
              <Button
                variant="secondary"
                size={{ base: 'sm', desktop: 'md' }}
                onClick={handleExportForTesting}
                isLoading={isDownloading}
                isDisabled={isDownloading}
              >
                Export for testing
              </Button>
            )}
            {isPalmettoUser && (
              <Button
                variant="secondary"
                size={{ base: 'sm', desktop: 'md' }}
                onClick={handleOpenCongaExportModal}
                isLoading={isCongaExporting}
                isDisabled={isCongaExporting}
              >
                Export Conga test CSV
              </Button>
            )}
          </Box>
        </Box>
        <AccountStatusIndicators account={account} />
        <Box direction="row" borderWidth="0 0 xs 0" borderColor="separator">
          <NavLink to={`/accounts/${id}`} style={{ textDecoration: 'none', fontWeight: '500' }} end>
            {({ isActive }) => (
              <Box
                as="span"
                padding="md"
                className={isActive ? 'tab-active' : ''}
                hover={{ color: 'primary' }}
                style={{ transitionDuration: '0s' }}
              >
                Overview
              </Box>
            )}
          </NavLink>
          {(isComfortPlan || showSolarCalculator) && (
            <NavLink to={`/accounts/${id}/quote-calculator`} style={{ textDecoration: 'none', fontWeight: '500' }}>
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={isActive ? 'tab-active' : ''}
                  hover={{ color: 'primary' }}
                  style={{ transitionDuration: '0s' }}
                >
                  Payment Calculator
                </Box>
              )}
            </NavLink>
          )}
          <NavLink to={`/accounts/${id}/system-design`} style={{ textDecoration: 'none', fontWeight: '500' }} end>
            {({ isActive }) => (
              <Box
                as="span"
                padding="md"
                className={isActive ? 'tab-active' : ''}
                hover={{ color: 'primary' }}
                style={{ transitionDuration: '0s' }}
                direction="row"
                childGap="xs"
              >
                {showPvSystemWarning && <Icon name="c-warning" size="md" color="danger" />}
                {getSystemDesignLabel(programType)}
              </Box>
            )}
          </NavLink>
          {!isQuotesLoading && hasActiveQuoteAdders && (
            <NavLink to={`/accounts/${id}/adders`} style={{ textDecoration: 'none', fontWeight: '500' }} end>
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={isActive ? 'tab-active' : ''}
                  hover={{ color: 'primary' }}
                  style={{ transitionDuration: '0s' }}
                >
                  Adders
                </Box>
              )}
            </NavLink>
          )}
          {payoutsEpcAdminView &&
          [
            UserPermissions.orgAccountingAdmin,
            UserPermissions.lightReachAccountingAdmin,
            UserPermissions.lightReachAccountingReader,
            UserPermissions.lightReachAccountingSettings,
          ].some((perm) => userPermissions.includes(perm)) ? (
            <NavLink to={`/accounts/${id}/funding`} style={{ textDecoration: 'none', fontWeight: '500' }}>
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={isActive ? 'tab-active' : ''}
                  hover={{ color: 'primary' }}
                  style={{ transitionDuration: '0s' }}
                >
                  Funding
                </Box>
              )}
            </NavLink>
          ) : null}
          {canViewWarrantiesTab && (
            <NavLink to={`/accounts/${id}/warranties`} style={{ textDecoration: 'none', fontWeight: '500' }}>
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={isActive ? 'tab-active' : ''}
                  hover={{ color: 'primary' }}
                  style={{ transitionDuration: '0s' }}
                >
                  Warranties
                </Box>
              )}
            </NavLink>
          )}
          {customerPayments &&
            userPermissions.includes(UserPermissions.lightReachSupport) &&
            hasConcordAccountBeenCreated && (
              <NavLink
                to={`/accounts/${id}/customer-payments`}
                style={{ textDecoration: 'none', fontWeight: '500' }}
              >
                {({ isActive }) => (
                  <Box
                    as="span"
                    padding="md"
                    className={isActive ? 'tab-active' : ''}
                    hover={{ color: 'primary' }}
                    style={{ transitionDuration: '0s' }}
                  >
                    Customer Payments
                  </Box>
                )}
              </NavLink>
            )}
          {viewAccountActivity && (
            <NavLink to={`/accounts/${id}/history`} style={{ textDecoration: 'none', fontWeight: '500' }}>
              {({ isActive }) => (
                <Box
                  as="span"
                  padding="md"
                  className={isActive ? 'tab-active' : ''}
                  hover={{ color: 'primary' }}
                  style={{ transitionDuration: '0s' }}
                >
                  History
                </Box>
              )}
            </NavLink>
          )}
        </Box>
        {wasMonthlyPaymentCapExceeded && (
          <Alert
            variant="warning"
            message={
              isAtMaxMonthlyPaymentCap
                ? 'Active quote is over the maximum monthly payment limit for this customer. Please void the quote and create a new one.'
                : 'Active quote is over the maximum monthly payment limit for this customer. Please either void the quote and create a new one or apply again with a co-applicant (or new co-applicant) to unlock the existing quote.'
            }
          ></Alert>
        )}

        <Routes>
          <Route path="/" element={<Overview />} />
          {payoutsEpcAdminView && <Route path="/funding" element={<AccountFundingDetails account={account} />} />}
          <Route path="/system-design" element={<SystemDesignFactory programType={programType} />} />
          <Route path="/adders" element={<AddersTab selectedAdders={activeQuote?.adders} />} />
          {(isComfortPlan || showSolarCalculator) && (
            <Route path="/quote-calculator" element={<PricingCalculatorFactory />} />
          )}
          {canViewWarrantiesTab && <Route path="/warranties" element={<Warranties />} />}
          {viewAccountActivity && <Route path="/history" element={<History />} />}
          {customerPayments && userPermissions.includes(UserPermissions.lightReachSupport) && (
            <Route path="/customer-payments" element={<CustomerPaymentTab account={account} />} />
          )}
        </Routes>
      </MainContainer>
      {isRejectLeadModalOpen && dispatchDispatchId && (
        <LeadManagementRejectLeadModal
          isOpen
          leadId={dispatchDispatchId}
          onDismiss={handleCloseRejectLeadModal}
          onConfirm={handleConfirmRejectLead}
          location={account?.primaryApplicantName ?? ''}
        />
      )}
      {isExportModalOpen && (
        <ExportDumpModal
          userEmail={user?.email ?? ''}
          onClose={() => setIsExportModalOpen(false)}
          onConfirm={handleExportConfirm}
        />
      )}
      {isCongaExportModalOpen && (
        <ExportCongaTestCsvModal
          userEmail={user?.email ?? ''}
          onClose={handleCloseCongaExportModal}
          onConfirm={handleCongaExportConfirm}
        />
      )}
      {isBundleModalOpen && account && (
        <CreateBundledAccountModal isOpen onDismiss={handleCloseBundleModal} sourceAccount={account} />
      )}
    </>
  );
}

interface ExportDumpModalProps {
  userEmail: string;
  onClose: () => void;
  onConfirm: (overrides: { firstName: string; lastName: string; email: string; phoneNumber: string }) => void;
}

function ExportDumpModal({ userEmail, onClose, onConfirm }: ExportDumpModalProps) {
  const [firstName, setFirstName] = useState('Home');
  const [lastName, setLastName] = useState('Demo');
  // Blank means "let the API generate homedemo+<lseId>-<accountId>@palmetto.com".
  // Duplicating that formula here would drift from the route that owns it.
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('5555555555');

  const handleUseMyEmail = useCallback(() => setEmail(userEmail), [userEmail]);

  const handleConfirm = useCallback(() => {
    onConfirm({ firstName, lastName, email, phoneNumber });
  }, [onConfirm, firstName, lastName, email, phoneNumber]);

  return (
    <Modal ariaLabelledBy="export-dump-modal-title" isOpen onDismiss={onClose} maxWidth="4xl">
      <Modal.Header id="export-dump-modal-title" title="Export for testing" onDismiss={onClose} />
      <Modal.Body childGap="md" background="secondary">
        <Box as="p" fontSize="sm" color="grey-500">
          PII is replaced in the export, and only the ZIP is carried — not the street address. Override the defaults
          below if needed.
        </Box>
        <Box direction="row" childGap="md">
          <TextInput
            id="export-first-name"
            label="First name"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <TextInput
            id="export-last-name"
            label="Last name"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </Box>
        <Box childGap="2xs">
          <TextInput
            id="export-email"
            label="Email"
            type="email"
            placeholder="homedemo+…@palmetto.com (generated)"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {userEmail && email !== userEmail && (
            <Box alignItems="flex-start">
              <Button size="sm" variant="tertiary" onClick={handleUseMyEmail}>
                Use my email ({userEmail})
              </Button>
            </Box>
          )}
        </Box>
        <TextInput
          id="export-phone"
          label="Phone number"
          value={phoneNumber}
          onChange={(e) => setPhoneNumber(e.target.value)}
        />
      </Modal.Body>
      <Modal.Footer justifyContent="flex-end" childGap="sm">
        <Button variant="secondary" tone="neutral" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleConfirm}>
          Export
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
